import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../lib/api';
import type { Round } from '../data/types';
import PlayView from './PlayView';

/* The public entry point: /real-or-fake/play/<token>.

   No login, by design — this opens on a recording machine, possibly in a different browser
   profile or an OBS browser source, and a sign-in redirect chain seconds before a take is a
   shoot-day outage.

   Fetch ONCE and freeze. No polling, no refetch on focus, no realtime: a round that changes
   itself mid-take is unacceptable, and a stale render is strictly better than a moving one. */

export default function PlayByToken() {
  const { token = '' } = useParams();
  const [round, setRound] = useState<Round | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const loaded = await api.play(token);
        if (!cancelled) setRound(loaded);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'This play link is no longer active.');
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  /* If this ever appears, it appears on camera — so it is quiet and branded, never a bare
     browser error. */
  if (error) {
    return (
      <div className="centred" data-testid="link-unavailable">
        <div className="round-title">Real or Fake?</div>
        <div className="big">Link not active</div>
        <div className="sub">{error} Ask whoever sent it for a fresh link.</div>
      </div>
    );
  }

  if (!round) {
    return (
      <div className="centred">
        <div className="big">Loading series…</div>
      </div>
    );
  }

  if (round.items.length === 0) {
    return (
      <div className="centred">
        <div className="round-title">Real or Fake?</div>
        <div className="big">This series is empty</div>
        <div className="sub">No headlines have been added to it yet.</div>
      </div>
    );
  }

  return <PlayView round={round} session={{ source: 'share', key: token }} />;
}
